/**
 * useControlScroll Hook
 *
 * Controls document scroll behavior for overlay UI elements (modals, drawers, fullscreen players).
 * Prevents background scrolling when overlays are active to improve UX and prevent scroll conflicts.
 *
 * Key architectural decisions:
 * - CSS class approach instead of overflow style manipulation for better specificity control
 * - Body-level control ensures all scrollable ancestors are affected
 * - Returns function instead of object for cleaner component integration
 *
 * Business requirements:
 * - Mobile users expect background to lock when modals open (iOS/Android pattern)
 * - Video player fullscreen mode must prevent accidental page scrolling
 * - Multiple overlays may stack - last one to close re-enables scroll
 *
 * WHY CSS class instead of style.overflow:
 * - CSS class can include additional properties (position: fixed, width: 100%)
 * - Prevents layout shift on Windows/Linux where scrollbar disappears
 * - Easier to override in specific scenarios via CSS specificity
 *
 * EDGE CASE: Multiple components using this hook:
 * - Not reference counted - last component to set state wins
 * - Components must properly cleanup on unmount to avoid stuck scroll
 * - Consider implementing reference counting if conflicts arise
 *
 * CSS Required (.no-scroll class definition):
 * body.no-scroll {
 *   overflow: hidden;
 *   position: fixed;   // Prevents iOS Safari bounce
 *   width: 100%;       // Prevents layout shift
 * }
 *
 * @returns {Function} controlScroll - Function accepting boolean to enable/disable scroll
 * @example
 * const controlScroll = useControlScroll();
 * // Disable scroll when modal opens
 * useEffect(() => {
 *   controlScroll(false);
 *   return () => controlScroll(true); // Re-enable on cleanup
 * }, []);
 */
export function useControlScroll(): (enabled: boolean) => void {
  function controlScroll(enabled: boolean): void {
    // WHY inverse boolean logic (enabled removes no-scroll):
    // Semantic API - "scroll enabled = true" is more intuitive than "no-scroll = false"
    // Reduces cognitive load when reading component code
    if (enabled) document.body.classList.remove('no-scroll');
    if (!enabled) document.body.classList.add('no-scroll');
  }

  // WHY return function directly instead of object:
  // Single responsibility - hook only does one thing
  // Cleaner destructuring: controlScroll vs { controlScroll }
  // Smaller bundle size - no object allocation
  return controlScroll;
}
